import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Clock, Flag, Eye } from "lucide-react";

export default function PhaseTaskDetail({ phase, tasks, onToggle, onAdd, onDelete, readOnly = false }) {
  const [newTitle, setNewTitle] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);

  const done = tasks.filter((t) => t.completed).length;
  const percent = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const visible = tasks.filter((t) =>
    filter === "pending" ? !t.completed : filter === "done" ? t.completed : true
  );

  const submit = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setAdding(true);
    try {
      await onAdd(phase.id, title);
      setNewTitle("");
    } finally {
      setAdding(false);
    }
  };

  const filters = [
    { key: "all", label: "Todas" },
    { key: "pending", label: "Pendientes" },
    { key: "done", label: "Hechas" },
  ];

  return (
    <div className="rounded-2xl border border-border bg-card" data-testid="phase-task-detail">
      <div className="p-6 sm:p-7 border-b border-border/70">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold">
            {phase.order}
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight leading-tight">{phase.name}</h2>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{phase.description}</p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="secondary" className="gap-1.5 font-normal">
                <Flag className="h-3 w-3 text-primary" /> {phase.critical_milestone}
              </Badge>
              <Badge variant="outline" className="gap-1.5 font-mono font-normal text-muted-foreground">
                <Clock className="h-3 w-3" /> {phase.estimated_duration}
              </Badge>
            </div>
          </div>
        </div>

        {/* Phase progress */}
        <div className="mt-5 flex items-center gap-3">
          <div className="h-2 flex-1 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-700"
              style={{ width: `${percent}%`, transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
              data-testid="phase-progress-bar"
            />
          </div>
          <span className="font-mono text-sm font-semibold w-20 text-right">
            {done}/{tasks.length} · {percent}%
          </span>
        </div>
      </div>

      <div className="p-6 sm:p-7">
        {/* Filters */}
        <div className="flex items-center gap-1 mb-4">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-sm px-3 py-1.5 rounded-full transition-colors ${
                filter === f.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-background-subtle"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Task list */}
        <div className="space-y-1.5">
          {visible.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">No hay tareas en este filtro.</p>
          )}
          {visible.map((task, i) => (
            <div
              key={task.task_id}
              data-testid={`task-row-${i}`}
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-background-subtle transition-colors"
            >
              <Checkbox
                data-testid={`task-checkbox-${i}`}
                checked={task.completed}
                disabled={readOnly}
                onCheckedChange={(v) => onToggle(task, Boolean(v))}
                className="h-5 w-5 disabled:opacity-80"
              />
              <div className="min-w-0 flex-1">
                <span className={`text-sm transition-colors ${task.completed ? "line-through text-muted-foreground" : ""}`}>
                  {task.title}
                </span>
              </div>
              <Badge variant="outline" className="hidden sm:inline-flex font-mono text-[10px] font-normal text-muted-foreground shrink-0">
                {task.category}
              </Badge>
              {!readOnly && (
                <button
                  data-testid={`delete-task-button-${i}`}
                  onClick={() => onDelete(task)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                  aria-label="Eliminar tarea"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {readOnly && (
          <p className="mt-5 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="read-only-notice">
            <Eye className="h-3.5 w-3.5" /> Vista de consulta: las tareas las gestiona el equipo del proyecto.
          </p>
        )}

        {/* Add custom task */}
        {!readOnly && <div className="mt-5 flex items-center gap-2">
          <Input
            data-testid="add-custom-task-input"
            placeholder="Añadir una tarea propia…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <Button
            data-testid="add-custom-task-button"
            onClick={submit}
            disabled={adding || !newTitle.trim()}
            className="rounded-full shrink-0 gap-1.5"
          >
            <Plus className="h-4 w-4" /> Añadir
          </Button>
        </div>}
      </div>
    </div>
  );
}
